import { useMemo, useState } from 'react';

import { isWebApplication } from '@/sections/asset/utils/assetTypeDetection';

// Re-export for components that import from this file
export { isWebApplication };
import { keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { DateRangeFilter } from '@/components/filters/types';
import { useApplicationDrawerEnabled } from '@/hooks/useApplicationDrawerEnabled';
import { useAxios } from '@/hooks/useAxios';
import { useGetAccountAlerts } from '@/hooks/useGetAccountAlerts';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useStableValue } from '@/hooks/useStableValue';
import { queryClient } from '@/queryclient';
import { buildGraphQuery } from '@/sections/insights/queryBuilder/legacy/builders/graphQueryBuilder';
import {
  EntityBlock,
  LogicalOperator,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { useAuth } from '@/state/auth';
import {
  Asset,
  AssetStatus,
  AssetWithWebpage,
  GraphQuery,
  NodeDetails,
  NodeLabel,
  Operator,
  RelationshipNode,
  SeverityOpenCounts,
  TreeNode,
} from '@/types';
import { useMergeStatus, useMutation } from '@/utils/api';
import { buildAttackSurfaceFilter } from '@/utils/attackSurface.util';
import { getRecentTimestamps } from '@/utils/date.util';
import { updateEntityInQueryExecutorCache } from '@/utils/queryExecutorCache';
import { getResourceType } from '@/utils/resourceType.util';
import { sanitizeFilterValue } from '@/utils/shared/filterTransformations.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Variables for updating an asset
 *
 * NOTE: Comments are stored as History records in the backend.
 * When adding a comment without changing status, the current status
 * will be fetched automatically and sent with the comment to create
 * a history record.
 *
 * @property key - Asset key (required)
 * @property name - Asset name for display in toasts (required)
 * @property status - New status (optional) - when provided, creates status transition
 * @property comment - Comment text (optional) - requires status to create history record
 * @property tags - Asset tags (optional) - handled separately via dedicated operations
 * @property showSnackbar - Show toast notification (optional)
 */
interface UpdateAssetProps {
  key: string;
  name: string;
  status?: AssetStatus;
  comment?: string;
  tags?: string[] | null;
  showSnackbar?: boolean;
}

/**
 * Extract Asset entities from TreeNode query results.
 *
 * Dynamically collects all resource arrays from the result object (assets, awsresources,
 * gcpresources, azureresources, etc.) that contain asset-like objects with required fields.
 * Filters out non-asset specialized types (integrations, repositories, webapplications).
 */
export const combineAssetNodeResults = (treeNode: TreeNode): Asset[] => {
  const result = treeNode.node?.result as Record<string, unknown> | undefined;
  if (!result) return [];
  const assets: Asset[] = [];

  // Iterate over all keys in result object to dynamically collect resource arrays
  for (const key in result) {
    const value = result[key];

    // Skip non-array values and metadata fields
    if (!Array.isArray(value) || key === 'count') {
      continue;
    }

    // Collect all objects that have asset-like properties (key and identifier)
    // Filter out specialized types that shouldn't appear in asset relationships
    const resourceAssets = value.filter((item: unknown) => {
      if (!item || typeof item !== 'object') return false;

      // Type guard: after checking it's an object, cast to access properties
      const obj = item as Record<string, unknown>;

      // Must have key and identifier to be considered an asset
      if (!obj.key || !obj.identifier) return false;

      // Filter out specialized types by checking labels
      const labels = obj.labels || [];
      const isSpecializedType = (labels as string[]).some((label: string) =>
        ['Integration', 'Repository', 'WebApplication'].includes(label)
      );

      return !isSpecializedType;
    });

    assets.push(...resourceAssets);
  }

  return assets.filter(Boolean);
};

/**
 * Extract Asset entities from RelationshipNode query results.
 *
 * Dynamically collects all resource arrays from the result object (assets, awsresources,
 * gcpresources, azureresources, etc.) that contain asset-like objects with required fields.
 * Filters out non-asset specialized types (integrations, repositories, webapplications).
 */
export const combineAssetRelationshipResults = (
  relationship: RelationshipNode
): Asset[] => {
  const result = relationship.target?.result as
    | Record<string, unknown>
    | undefined;
  if (!result) return [];
  const assets: Asset[] = [];

  // Iterate over all keys in result object to dynamically collect resource arrays
  for (const key in result) {
    const value = result[key];

    // Skip non-array values and metadata fields
    if (!Array.isArray(value) || key === 'count') {
      continue;
    }

    // Collect all objects that have asset-like properties (key and identifier)
    // Filter out specialized types that shouldn't appear in asset relationships
    const resourceAssets = value.filter((item: unknown) => {
      if (!item || typeof item !== 'object') return false;

      // Type guard: after checking it's an object, cast to access properties
      const obj = item as Record<string, unknown>;

      // Must have key and identifier to be considered an asset
      if (!obj.key || !obj.identifier) return false;

      // Filter out specialized types by checking labels
      const labels = obj.labels || [];
      const isSpecializedType = (labels as string[]).some((label: string) =>
        ['Integration', 'Repository', 'WebApplication'].includes(label)
      );

      return !isSpecializedType;
    });

    assets.push(...resourceAssets);
  }

  return assets.filter(Boolean);
};

// Re-export getFilterMap for backward compatibility (used by ExportAssets)
export const getFilterMap = (): Record<
  string,
  NonNullable<NodeDetails['filters']>[number]
> => {
  const timestamps = getRecentTimestamps();
  return {
    A: {
      field: '',
      operator: 'AND',
      value: [
        { field: 'status', operator: '=', value: 'A' },
        {
          field: 'visited',
          operator: '>=',
          value: timestamps['48h'],
        },
      ],
    },
    [AssetStatus.Unavailable]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Unavailable,
    },
    IN: {
      field: '',
      operator: 'AND',
      value: [
        { field: 'status', operator: '=', value: 'A' },
        {
          field: 'visited',
          operator: '<',
          value: timestamps['48h'],
        },
        {
          field: 'visited',
          operator: '>=',
          value: timestamps['7days'],
        },
      ],
    },
    I: {
      field: '',
      operator: 'AND',
      value: [
        { field: 'status', operator: '=', value: 'A' },
        {
          field: 'visited',
          operator: '<',
          value: timestamps['7days'],
        },
      ],
    },
    [AssetStatus.Pending]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Pending,
    },
    [AssetStatus.Frozen]: {
      field: 'status',
      operator: 'STARTS WITH',
      value: AssetStatus.Frozen,
    },
    [AssetStatus.Deleted]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Deleted,
    },
  };
};

export const useUpdateAsset = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<void, Error, UpdateAssetProps>({
    defaultErrorMessage: 'Failed to update asset',
    mutationFn: async ({ key, name, status, comment, showSnackbar = true }) => {
      let promise;
      if (status === AssetStatus.Deleted) {
        promise = axios.delete(`/asset`, {
          data: {
            key,
          },
        });

        if (showSnackbar) {
          toast.promise(promise, {
            loading: `Deleting ${name}`,
            success: `Deleted ${name}`,
            error: `Failed to delete ${name}`,
          });
        }

        await promise;
      } else {
        // Build request body with only the fields that are provided
        const body: {
          key: string;
          type: string;
          name?: string;
          status?: string;
          comment?: string;
          tags?: string[] | null;
        } = {
          key,
          type: getResourceType(key), // Extract type from key format
        };

        // Add name if provided
        if (name !== undefined) body.name = name;

        // When adding a comment without changing status, we need to fetch current status
        // because backend requires BOTH status and comment to create history record
        if (comment !== undefined && status === undefined) {
          // Performance note: This code path should rarely execute if callers pass status from cached data
          console.warn(
            '[useUpdateAsset] Status not provided with comment, fetching from API (performance impact)'
          );

          try {
            // Fetch current asset to get its status
            const currentAsset = await axios.get(`/my`, {
              params: {
                label: 'asset',
                key: key,
                exact: true,
              },
            });
            const currentStatus = currentAsset.data.assets?.[0]?.status || 'A';
            body.status = currentStatus; // Include current status
            body.comment = comment;
          } catch (error) {
            // If status fetch fails, default to Active status
            // This allows comment to be added even if fetch fails
            console.warn(
              '[useUpdateAsset] Failed to fetch current status, defaulting to Active:',
              error
            );
            body.status = 'A';
            body.comment = comment;
          }
        } else {
          // Normal status update (with or without comment)
          if (status !== undefined) body.status = status;
          if (comment !== undefined) body.comment = comment;
        }
        // Tags are handled separately via dedicated tag operations
        // Removed: body.tags = null - This was causing 400 errors for comment operations

        promise = axios.put(`/asset`, body);

        if (showSnackbar) {
          toast.promise(promise, {
            loading: `Updating ${name}`,
            success: `Updated ${name}`,
            error: `Failed to update ${name}`,
          });
        }
      }

      await promise;
      invalidateAlerts();

      // Optimistic cache update for query-executor (instant UI feedback)
      const optimisticUpdate: { key: string; [k: string]: unknown } = { key };
      if (status !== undefined) optimisticUpdate.status = status;
      if (name !== undefined) optimisticUpdate.name = name;
      updateEntityInQueryExecutorCache(queryClient, 'assets', optimisticUpdate);

      // Invalidate all asset queries (table, drawer, filters)
      // Using same pattern as useAddComment and useDeleteAsset for consistency
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    },
  });
};

export const useDeleteAsset = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<void, Error, UpdateAssetProps>({
    defaultErrorMessage: 'Failed to delete asset',
    mutationFn: async ({ key, name }) => {
      const promise = axios.delete(`/asset`, {
        data: {
          key,
        },
      });

      toast.promise(promise, {
        loading: `Deleting ${name}`,
        success: `Deleted ${name}`,
        error: `Failed to delete ${name}`,
      });

      await promise;

      invalidateAlerts();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    },
  });
};

export const useCreateAsset = () => {
  const axios = useAxios();
  const { invalidate: invalidateJob } = useMy(
    { resource: 'job' },
    { enabled: false }
  );
  const { invalidate: invalidateProvidedAssets } = useMy({
    resource: 'attribute',
    query: '#surface#provided',
  });

  return useMutation<Asset, Error, Pick<Asset, 'identifier' | 'status'>>({
    defaultErrorMessage: `Failed to add asset`,
    getErrorFromResponse: true,
    mutationFn: async asset => {
      const promise = axios.put(`/asset`, {
        dns: asset.identifier,
        name: asset.identifier,
        status: asset.status || AssetStatus.Active,
        tags: null,
      });

      toast.promise(promise, {
        loading: 'Adding asset',
        success: `Added ${asset.identifier}`,
      });

      const { data } = await promise;

      invalidateJob();
      invalidateProvidedAssets();

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });

      return data;
    },
    onError: error => {
      toast.error('Failed to add asset', {
        description: `${error.name} ${error.message}`,
      });
    },
  });
};

export type PartialAsset = Pick<
  Asset,
  'visited' | 'key' | 'group' | 'identifier'
> & {
  riskSummary?: SeverityOpenCounts;
};

export interface Filters {
  search?: string;
  riskKey?: string;
  advancedFilters?: {
    // Asset Attribute filters
    new?: string[];
    port?: string[];
    protocol?: string[];
    tags?: string[];
    // Asset module filters
    asname?: string[];
    asnumber?: string[];
    status?: AssetStatus[];
    attackSurface?: string[];
    source?: string[];
    origins?: string[];
    // Scan profile filter
    profile?: string[];
    // Technology filters
    vendor?: string[];
    product?: string[];
    // Risk scoring filters
    riskSeverity?: string[];
    // Date range filter
    dateRange?: DateRangeFilter;
  };
  sort?: {
    orderBy: keyof Asset;
    descending: boolean;
  };
}

// Convert Asset Table filters to QueryBuilder EntityBlocks for buildGraphQuery
function convertFiltersToEntityBlocks(
  filters: Filters,
  excludeAccountSources: boolean,
  excludeApplication: boolean
): EntityBlock[] {
  const blocks: EntityBlock[] = [];

  // Create main Asset entity block
  const assetBlock: EntityBlock = {
    id: 'asset-block',
    entityType: 'Asset',
    filterGroups: [],
  };

  // Add status filters
  if (
    filters.advancedFilters?.status &&
    filters.advancedFilters.status.length > 0
  ) {
    assetBlock.filterGroups.push({
      id: 'status-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: filters.advancedFilters.status.map((status, index) => ({
        id: `status-${index}`,
        attribute: 'status',
        operator: 'equals',
        value: status,
      })),
    });
  }

  // Add other simple Asset filters
  // Exclude specific fields (status, vendor, product, source, new, protocol, port) from simpleFilters
  // These are handled separately via dedicated filter logic
  // Note: protocol and port require HAS_PORT relationship queries (PR #3413)
  const {
    status: _status, // eslint-disable-line @typescript-eslint/no-unused-vars
    vendor: _vendor, // eslint-disable-line @typescript-eslint/no-unused-vars
    product: _product, // eslint-disable-line @typescript-eslint/no-unused-vars
    source: _source, // eslint-disable-line @typescript-eslint/no-unused-vars
    new: _new, // eslint-disable-line @typescript-eslint/no-unused-vars
    protocol: _protocol, // eslint-disable-line @typescript-eslint/no-unused-vars
    port: _port, // eslint-disable-line @typescript-eslint/no-unused-vars
    dateRange: _dateRange, // eslint-disable-line @typescript-eslint/no-unused-vars
    attackSurface: _attackSurface, // eslint-disable-line @typescript-eslint/no-unused-vars
    profile: _profile, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...simpleFilters
  } = filters.advancedFilters || {};

  // Handle 'new' filter specially - map to 'created' field with 'in the last' operator
  if (filters.advancedFilters?.new && filters.advancedFilters.new.length > 0) {
    assetBlock.filterGroups.push({
      id: 'created-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: filters.advancedFilters.new.map((val, index) => ({
        id: `created-${index}`,
        attribute: 'created',
        operator: 'in the last',
        value: val,
      })),
    });
  }

  // Handle 'dateRange' filter - convert to created/visited field with >= and <= operators
  if (filters.advancedFilters?.dateRange) {
    const { field, startDate, endDate } = filters.advancedFilters.dateRange;
    assetBlock.filterGroups.push({
      id: 'date-range-group',
      logicalOperator: 'AND' as LogicalOperator,
      filters: [
        {
          id: 'date-range-start',
          attribute: field,
          operator: 'greater than or equals',
          value: `${startDate}T00:00:00Z`,
        },
        {
          id: 'date-range-end',
          attribute: field,
          operator: 'less than or equals',
          value: `${endDate}T23:59:59Z`,
        },
      ],
    });
  }

  // Handle 'protocol' filter - requires HAS_PORT relationship (PR #3413)
  // Protocol/service names are stored in Port nodes via HAS_PORT relationship
  // processFilter will handle creating the HAS_PORT relationship with Port.service filter
  if (
    filters.advancedFilters?.protocol &&
    filters.advancedFilters.protocol.length > 0
  ) {
    assetBlock.filterGroups.push({
      id: 'protocol-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: filters.advancedFilters.protocol.map((val, index) => ({
        id: `protocol-${index}`,
        attribute: 'protocol',
        operator: 'equals',
        value: val,
      })),
    });
  }

  // Handle 'port' filter - requires HAS_PORT relationship (PR #3413)
  // Port numbers are stored in Port nodes via HAS_PORT relationship
  // processFilter will handle creating the HAS_PORT relationship with Port.port filter
  if (
    filters.advancedFilters?.port &&
    filters.advancedFilters.port.length > 0
  ) {
    assetBlock.filterGroups.push({
      id: 'port-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: filters.advancedFilters.port.map((val, index) => ({
        id: `port-${index}`,
        attribute: 'port',
        operator: 'equals',
        value: val,
      })),
    });
  }

  // Note: 'attackSurface' filter is handled separately by injecting boolean filters
  // directly into convertedQuery to avoid EntityBlock type constraints (ENG-1862)

  // Handle profile filter — empty string means IS NULL (no profile assigned)
  if (
    filters.advancedFilters?.profile &&
    filters.advancedFilters.profile.length > 0
  ) {
    const profileFilters = filters.advancedFilters.profile.map(
      (val: string, index: number) => ({
        id: `profile-${index}`,
        attribute: 'profile',
        operator: val === '' ? ('not exists' as const) : ('equals' as const),
        value: val === '' ? undefined : val,
      })
    );
    assetBlock.filterGroups.push({
      id: 'profile-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: profileFilters,
    });
  }

  Object.entries(simpleFilters).forEach(([key, value]) => {
    if (value && Array.isArray(value) && value.length > 0) {
      assetBlock.filterGroups.push({
        id: `${key}-group`,
        logicalOperator: 'OR' as LogicalOperator,
        filters: value.map((val, index) => ({
          id: `${key}-${index}`,
          attribute: key,
          operator: key === 'tags' ? 'is any of' : 'equals',
          value: val,
        })),
      });
    }
  });

  // Add excludeAccountSources filter
  if (excludeAccountSources && !filters.riskKey) {
    assetBlock.filterGroups.push({
      id: 'exclude-account-group',
      logicalOperator: 'AND' as LogicalOperator,
      filters: [
        {
          id: 'exclude-account',
          attribute: 'source',
          operator: 'not equals',
          value: 'account',
        },
      ],
    });
  }

  // Exclude Application rollup rows from the asset table at the graph
  // layer. They carry :Asset (per #5663) but the new Application drawer
  // isn't shipped yet, so showing them as table rows is broken UX.
  // Filtering server-side keeps pagination honest — the LIMIT counts
  // only non-Application rows. Praetorian devs can still preview by
  // flipping the application_drawer Statsig gate on.
  if (excludeApplication && !filters.riskKey) {
    assetBlock.filterGroups.push({
      id: 'exclude-application-group',
      logicalOperator: 'AND' as LogicalOperator,
      filters: [
        {
          id: 'exclude-application',
          attribute: 'class',
          operator: 'not equals',
          value: 'application',
        },
      ],
    });
  }

  blocks.push(assetBlock);

  return blocks;
}

export const defaultFilters: Filters = {
  advancedFilters: {},
  sort: {
    orderBy: 'visited',
    descending: true,
  },
};

export function useQueryAssets({
  enabled = true,
  limit = 100,
  parentFilters,
  excludeAccountSources = false,
}: {
  enabled?: boolean;
  limit?: number;
  parentFilters?: Filters;
  excludeAccountSources?: boolean;
} = {}) {
  // When parentFilters exist (drawer context), use ephemeral state so drawer
  // filters never leak to the main table. Every drawer open starts fresh.
  const isDrawerContext = !!parentFilters;

  const { isPraetorianUser } = useAuth();
  const applicationDrawerEnabled = useApplicationDrawerEnabled();

  const [persistedFilters, setPersistedFilters] = useStorage<Filters>(
    {
      queryKey: 'assetFiltersV3',
      key: StorageKey.FILTERS_ASSETS_V3,
      ephemeralKeys: ['search'],
    },
    defaultFilters
  );

  // Clean up stale riskScore sort that may have been saved in localStorage
  if ((persistedFilters.sort?.orderBy as string) === 'riskScore') {
    setPersistedFilters({
      ...persistedFilters,
      sort: defaultFilters.sort,
    });
  }

  // Sanitize stale status filters: non-Praetorian users should never see the
  // Deleted ('D') status. If a customer previously had 'D' in their persisted
  // filters (e.g., from a Praetorian impersonation session) strip it out so it
  // does not silently ghost their results.
  const sanitizedPersistedFilters: Filters = useMemo(() => {
    if (isPraetorianUser) return persistedFilters;
    const status = persistedFilters.advancedFilters?.status;
    if (!status || !status.includes(AssetStatus.Deleted))
      return persistedFilters;
    return {
      ...persistedFilters,
      advancedFilters: {
        ...persistedFilters.advancedFilters,
        status: status.filter(s => s !== AssetStatus.Deleted),
      },
    };
  }, [isPraetorianUser, persistedFilters]);

  const [ephemeralFilters, setEphemeralFilters] =
    useState<Filters>(defaultFilters);

  const localFilters = isDrawerContext
    ? ephemeralFilters
    : sanitizedPersistedFilters;
  const setFilters = isDrawerContext
    ? setEphemeralFilters
    : setPersistedFilters;

  const filters: Filters = parentFilters
    ? { ...parentFilters, ...localFilters }
    : localFilters;
  // BUGFIX: Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const [debouncedSearch] = useDebounce(
    filters.search,
    filters.search === '' ? 0 : 300
  );

  const stableAdvancedFilters = useStableValue(filters.advancedFilters);

  const { convertedQuery, isFiltersApplied, searchParam } = useMemo(() => {
    // Build search param for fulltext index (assetSearchIndex covers key, name, dns, identifier)
    const searchParam =
      debouncedSearch && debouncedSearch.length >= 2
        ? debouncedSearch
        : undefined;

    // Convert Asset Table filters to QueryBuilder EntityBlocks
    const entityBlocks = convertFiltersToEntityBlocks(
      filters,
      excludeAccountSources,
      !applicationDrawerEnabled
    );

    // Use QueryBuilder's proven buildGraphQuery logic
    const graphQuery = buildGraphQuery(
      entityBlocks,
      [], // No inter-group operators for simple case
      {
        // Asset-specific options
        ...(stableAdvancedFilters?.vendor && {
          technologyVendorValues: stableAdvancedFilters.vendor,
        }),
        ...(stableAdvancedFilters?.product && {
          technologyProductValues: stableAdvancedFilters.product,
        }),
        ...(stableAdvancedFilters?.source && {
          sourceValues: stableAdvancedFilters.source,
        }),
      },
      undefined, // vulnFilterOptions
      undefined, // technologyFilterOptions
      undefined, // seedFilterOptions
      undefined, // integrationValues
      undefined, // queryContentTab
      filters.riskKey // Pass riskKey to buildGraphQuery
    );

    const hasFilters = entityBlocks.some(
      block =>
        block.filterGroups.length > 0 ||
        (stableAdvancedFilters &&
          Object.keys(stableAdvancedFilters).length > 0) ||
        !!debouncedSearch ||
        excludeAccountSources ||
        !!filters.riskKey
    );

    // Build attack surface boolean filters (ENG-1862)
    // Bypasses EntityBlock to use proper boolean values for Neo4j RANGE indexes
    const surfaceFilterNode = filters.advancedFilters?.attackSurface?.length
      ? (buildAttackSurfaceFilter(
          filters.advancedFilters.attackSurface
        ) as QueryFilter)
      : null;

    // Convert QueryBuilder's GraphQuery to useMy-compatible format
    const convertedQuery = graphQuery
      ? {
          ...graphQuery,
          node: {
            ...graphQuery.node,
            labels: graphQuery.node.labels as NodeLabel[],
            // Fulltext search — covers key, name, dns, identifier via assetSearchIndex
            ...(searchParam ? { search: searchParam } : {}),
            filters: [
              ...(graphQuery.node.filters?.map((filter: QueryFilter) => ({
                ...filter,
                operator: filter.operator as Operator,
              })) ?? []),
              ...(surfaceFilterNode ? [surfaceFilterNode] : []),
            ],
            relationships: graphQuery.node.relationships?.map(
              (rel: Relationship) => ({
                ...rel,
                target: rel.target
                  ? {
                      ...rel.target,
                      labels: rel.target.labels as NodeLabel[],
                      filters: rel.target.filters?.map(
                        (filter: QueryFilter) => ({
                          ...filter,
                          operator: filter.operator as Operator,
                        })
                      ),
                    }
                  : undefined,
              })
            ),
          },
        }
      : null;

    return {
      convertedQuery,
      isFiltersApplied: hasFilters,
      searchParam,
    };
  }, [
    filters,
    stableAdvancedFilters,
    debouncedSearch,
    excludeAccountSources,
    applicationDrawerEnabled,
  ]);

  // Only use tree query when port/protocol filters are active (requires HAS_PORT relationships).
  // tree: true triggers ScanTree on the backend which runs expensive Neo4j path queries.
  // For the default view (no port/protocol filters), Scan is sufficient and much faster.
  const needsTreeQuery = Boolean(
    filters.advancedFilters?.port?.length ||
      filters.advancedFilters?.protocol?.length
  );

  const query = useMy(
    {
      resource: 'asset',
      tree: needsTreeQuery || undefined,
      query:
        convertedQuery && isFiltersApplied
          ? ({
              // Construct proper GraphQuery matching Asset Table interface
              node: {
                ...convertedQuery.node,
                filters: convertedQuery.node.filters?.map(
                  (filter: QueryFilter) => ({
                    ...filter,
                    value: sanitizeFilterValue(filter.value) || filter.value,
                  })
                ),
              },
              limit: limit,
              page: 0,
              ...(filters.sort && filters.sort.orderBy === 'status'
                ? {
                    orderByFields: [
                      {
                        field: 'visited',
                        descending: true,
                      },
                    ],
                  }
                : filters.sort && filters.sort.orderBy !== 'status'
                  ? {
                      orderBy: filters.sort.orderBy,
                      descending: filters.sort.descending,
                    }
                  : {}),
            } as GraphQuery)
          : ({
              node: {
                labels: ['Asset'] as NodeLabel[],
                ...(searchParam ? { search: searchParam } : {}),
              },
              limit: limit,
              page: 0,
              ...(filters.sort && filters.sort.orderBy === 'status'
                ? {
                    orderByFields: [
                      {
                        field: 'visited',
                        descending: true,
                      },
                    ],
                  }
                : filters.sort && filters.sort.orderBy !== 'status'
                  ? {
                      orderBy: filters.sort.orderBy,
                      descending: filters.sort.descending,
                    }
                  : {}),
            } as GraphQuery),
    },
    {
      enabled,
      // Keep previous data visible during search to prevent blanking on every keystroke
      // Search is excluded from filterKey, so table doesn't remount
      // Filter/sort changes still remount via filterKey for scroll reset
      placeholderData: keepPreviousData,
      staleTime: 30_000,
    }
  );

  const assets = useMemo(() => {
    if (!query.data?.length) return [];

    if (needsTreeQuery) {
      // Tree format: extract assets from tree nodes and merge relationship data
      const result = query.data
        .map((tree): AssetWithWebpage | null => {
          const entities = combineAssetNodeResults(tree);

          // Extract webpage from relationships (legacy pattern)
          const webpage =
            tree?.node?.relationships?.[0]?.target?.result?.webpages?.[0];

          // Extract ports from HAS_PORT relationships (PR #3413)
          // When filtering by port/protocol, the Port nodes are returned in relationships
          const ports = tree?.node?.relationships
            ?.filter((rel: RelationshipNode) => rel.label === 'HAS_PORT')
            .flatMap((rel: RelationshipNode) => rel.target?.result?.ports || [])
            .filter(Boolean);

          // Safe access to first entity
          const firstEntity = entities[0];
          if (!firstEntity) {
            return null; // Return null instead of empty object
          }

          // Merge Asset with extracted ports
          return {
            ...firstEntity,
            webpage,
            // Include ports from relationships (takes precedence over Asset's ports field)
            ...(ports && ports.length > 0 && { ports }),
          } as AssetWithWebpage;
        })
        .filter((asset): asset is AssetWithWebpage => asset !== null);

      // BUGFIX: Deduplicate assets by key to prevent duplicate rows in table
      // This handles cases where graph queries with OPTIONAL relationships return
      // the same asset multiple times (once per matched relationship).
      // Example: Asset with 2 HAS_PORT relationships returns 2 rows from Neo4j.
      // Related: CHARIOT-1567
      const seenKeys = new Set<string>();
      return result.filter(asset => {
        if (seenKeys.has(asset.key)) {
          return false; // Skip duplicate
        }
        seenKeys.add(asset.key);
        return true; // Keep first occurrence
      });
    }

    // Flat format: data is already Asset objects from handleGraphQueryResponse in useMy
    return query.data as AssetWithWebpage[];
  }, [query.data, needsTreeQuery]);

  // Assets associated to a specific risk, we do it because risk can exist on an asset or its attribute.
  // If filters.riskKey is provided, above query gets assets via risk attribute, below query gets assets directly linked to risk
  // BUGFIX: Apply user-selected filters (status, search, tags, attackSurface) to this query as well
  const riskAssetQuery = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'] as NodeLabel[],
          // Apply same user filters as main query (with sanitization), fallback to exclude deleted assets
          filters: convertedQuery?.node?.filters
            ? convertedQuery.node.filters.map((filter: QueryFilter) => ({
                ...filter,
                value: sanitizeFilterValue(filter.value) || filter.value,
              }))
            : [
                {
                  field: 'status',
                  operator: '=' as Operator,
                  value: AssetStatus.Deleted,
                  not: true,
                },
              ],
          relationships: [
            {
              label: 'HAS_VULNERABILITY' as const,
              target: {
                labels: ['Risk'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: filters.riskKey || '',
                  },
                ],
              },
            },
          ],
        },
      } as GraphQuery,
    },
    {
      enabled: enabled && Boolean(filters.riskKey),
      placeholderData: keepPreviousData,
      staleTime: 30_000,
    }
  );

  const combinedData = [...assets, ...riskAssetQuery.data];
  // Applications are excluded server-side via the exclude-application
  // filter group in convertFiltersToEntityBlocks (gated by
  // !applicationDrawerEnabled). No client-side post-filter needed.
  const data = combinedData;
  const status = useMergeStatus(
    query.status,
    filters.riskKey ? riskAssetQuery.status : 'success'
  );

  return {
    data,
    error: query.error || riskAssetQuery.error,
    status,
    isLoading: query.isLoading,
    isFetching: query.isFetching || riskAssetQuery.isFetching,
    fetchNextPage: () => {
      query.fetchNextPage();
      if (filters.riskKey) {
        riskAssetQuery.fetchNextPage();
      }
    },
    isFetchingNextPage:
      query.isFetchingNextPage || riskAssetQuery.isFetchingNextPage,
    hasNextPage: query.hasNextPage || riskAssetQuery.hasNextPage,
    refetch: () => {
      query.refetch();
      if (filters.riskKey) {
        riskAssetQuery.refetch();
      }
    },
    invalidate: () => {
      query.invalidate();
      if (filters.riskKey) {
        riskAssetQuery.invalidate();
      }
    },
    isFilteredEmpty: status !== 'pending' && data.length === 0,
    filters,
    setFilters,
    isFiltersApplied,
    convertedQuery, // QueryBuilder's graph query converted to useMy format
    activeQuery: convertedQuery, // Expose as activeQuery for export modal
  };
}
